import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, PHONE, PHONE_INTL, IMAGES } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { Reveal } from "../components/Reveal";
import { Breadcrumbs, CtaBand, FaqAccordion } from "../components/Shared";
import { CallButton, PhoneBox } from "../components/Layout";
import ContactForm from "../components/ContactForm";
import BrandsSection from "../components/BrandsSection";
import ArticlesTeaser from "../components/ArticlesTeaser";
import ReviewsSection from "../components/Reviews";
import { MapPin, Clock, ShieldCheck, ArrowUpRight, Phone, Check } from "lucide-react";

const SERVICE_LINKS = [
  { slug: "depannage-serrurerie", label: (n) => `Dépannage serrurier ${n}` },
  { slug: "depannage-serrurerie", label: (n) => `Urgence serrurier ${n}` },
  { slug: "ouverture-de-porte", label: (n) => `Ouverture de porte ${n}` },
  { slug: "changement-de-serrure", label: (n) => `Remplacement serrure ${n}` },
  { slug: "blindage-de-porte", label: (n) => `Blindage de porte ${n}` },
  { slug: "reparation-apres-effraction", label: (n) => `Après effraction ${n}` },
  { slug: "mise-en-securite", label: (n) => `Mise en sécurité ${n}` },
];

export default function CityPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/cities/${slug}`).then(r => setData(r.data)).catch(() => setData(false));
    api.get("/services").then(r => setServices(r.data)).catch(() => {});
    api.get("/cities").then(r => setCities(r.data)).catch(() => {});
  }, [slug]);

  const content = data && data.content;
  useSEO({
    title: content ? content.title : "Serrurier Seine-et-Marne",
    description: content ? content.meta_description : "",
    path: `/serrurier/${slug}`, faq: content ? content.faq : null, city: data ? data.city : null,
    keywords: content ? content.keywords : null,
  });

  if (data === false) return <div className="pt-48 pb-32 text-center bg-white"><h1 className="text-4xl font-display text-zinc-900">Ville introuvable</h1><Link to="/villes" className="text-primary mt-4 inline-block">← Toutes les villes</Link></div>;
  if (!data) return <div className="pt-40 pb-40 text-center text-zinc-400 bg-white">Chargement…</div>;
  const { city, voisins } = data;

  const gallery = [
    { src: IMAGES.tech, alt: `Serrurier SERRURERIE 77 en intervention à ${city.name}`, cap: "Intervention à domicile" },
    { src: IMAGES.cylinder, alt: `Changement de cylindre de serrure à ${city.name}` , cap: "Changement de serrure" },
    { src: IMAGES.handleDark, alt: `Serrure multipoints et blindage de porte à ${city.name}`, cap: "Blindage & sécurité" },
    { src: IMAGES.van, alt: `Véhicule SERRURERIE 77 pour dépannage à ${city.name}`, cap: "Dépannage 7j/7" },
  ];

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative pt-32 pb-16 sm:pt-36 hero-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Breadcrumbs items={[{ label: "Accueil", to: "/" }, { label: "Zones d'intervention", to: "/villes" }, { label: city.name }]} />
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-6">
              <MapPin className="h-3.5 w-3.5" /> {city.cp} · Seine-et-Marne · {city.secteur}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-semibold tracking-tighter leading-[0.98] text-zinc-900">{content.h1}</h1>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-xl">{content.lead}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              {["Intervention 30 min", "Devis gratuit", "7j/7 · 24h/24"].map((t) => (
                <span key={t} className="flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm text-zinc-700 shadow-sm"><Check className="h-4 w-4 text-primary" /> {t}</span>
              ))}
            </div>
            <div className="mt-8"><PhoneBox /></div>
          </div>
          <Reveal delay={0.1} className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-zinc-300/60 border border-zinc-200">
              <img src={IMAGES.techHandle} alt={`Serrurier professionnel à ${city.name} en Seine-et-Marne`} className="w-full h-[420px] sm:h-[500px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-2xl bg-white shadow-xl border border-zinc-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="grid place-items-center h-11 w-11 rounded-full bg-primary/10"><Clock className="h-5 w-5 text-primary" /></div>
                <div><div className="font-display font-semibold text-lg text-zinc-900 leading-none">Serrurier {city.name}</div><div className="text-xs text-zinc-500 mt-1">Intervention en ~30 min</div></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GALLERY */}
      <section className="relative z-10 py-16 bg-zinc-50 border-y border-zinc-200" data-testid="city-gallery">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal><p className="overline mb-6">Nos interventions à {city.name}</p></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.06} className="group relative overflow-hidden rounded-2xl border border-zinc-200 shadow-sm">
                <img src={g.src} alt={g.alt} className="w-full h-44 sm:h-52 object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 to-transparent" />
                <span className="absolute bottom-3 left-3 text-xs font-medium text-white">{g.cap}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENT + SIDEBAR */}
      <section className="relative z-10 py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1.6fr_1fr] gap-14">
          <article className="max-w-3xl">
            {content.sections.map((sec, i) => (
              <Reveal key={i} className="mb-14">
                <h2 className="text-3xl sm:text-4xl font-display font-medium tracking-tight mb-6 text-zinc-900">{sec.h2}</h2>
                {sec.paragraphs.map((p, j) => <p key={j} className="text-zinc-600 leading-relaxed mb-5 text-[1.05rem]">{p}</p>)}
                {sec.h3blocks && (
                  <div className="mt-8 grid sm:grid-cols-1 gap-5">
                    {sec.h3blocks.map((b, k) => (
                      <div key={k} className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6">
                        <h3 className="text-xl font-display font-medium mb-2 text-zinc-900">{b.h3}</h3>
                        <p className="text-zinc-600 leading-relaxed">{b.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Reveal>
            ))}

            <Reveal className="mb-4">
              <h2 className="text-3xl sm:text-4xl font-display font-medium tracking-tight mb-6 text-zinc-900">Nos services de serrurier à {city.name}</h2>
              <div className="flex flex-wrap gap-2.5">
                {SERVICE_LINKS.map((s, i) => (
                  <Link key={i} to={`/services/${s.slug}`} data-testid={`city-service-link-${i}`} className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm text-zinc-700 hover:text-primary hover:border-primary transition-colors flex items-center gap-1.5 shadow-sm">{s.label(city.name)}<ArrowUpRight className="h-3 w-3" /></Link>
                ))}
              </div>
            </Reveal>
          </article>

          <aside className="lg:sticky lg:top-28 h-fit space-y-6">
            <div className="rounded-2xl bg-primary p-7 shadow-xl shadow-primary/20 text-white">
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/70 mb-2">Urgence à {city.name}</p>
              <a href={`tel:${PHONE_INTL}`} className="block font-display font-semibold text-4xl tracking-tighter hover:opacity-90 transition-opacity">{PHONE}</a>
              <p className="text-sm text-indigo-100 mt-3">Un serrurier se met en route dès votre appel.</p>
              <a href={`tel:${PHONE_INTL}`} data-testid="city-sidebar-call" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white py-3.5 font-semibold text-primary hover:scale-[1.02] transition-transform"><Phone className="h-4 w-4" /> Appeler maintenant</a>
            </div>
            {voisins.length > 0 && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-7 shadow-sm">
                <p className="overline mb-4">Villes voisines</p>
                <div className="flex flex-col gap-2.5">
                  {voisins.map(v => <Link key={v.slug} to={`/serrurier/${v.slug}`} className="text-zinc-600 hover:text-primary transition-colors flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-primary" /> Serrurier {v.name}</Link>)}
                </div>
              </div>
            )}
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-7">
              <ShieldCheck className="h-6 w-6 text-primary mb-3" />
              <p className="font-medium text-zinc-900">Serrures certifiées A2P</p>
              <p className="text-sm text-zinc-500 mt-1">Toutes marques · pièces garanties · agréé assurances.</p>
            </div>
          </aside>
        </div>
      </section>

      {/* CONTACT */}
      <section className="relative z-10 py-20 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <p className="overline mb-4">Devis gratuit à {city.name}</p>
            <h2 className="text-4xl font-display font-semibold tracking-tighter mb-8 text-zinc-900">Demandez votre intervention</h2>
          </Reveal>
          <Reveal delay={0.1}><ContactForm services={services} cities={cities} defaultCity={city.name} /></Reveal>
        </div>
      </section>

      <FaqAccordion items={content.faq} title={`Questions fréquentes — Serrurier ${city.name}`} subtitle={`Les réponses aux questions les plus posées par les habitants de ${city.name}.`} />
      <ReviewsSection subtitle={`Les avis de nos clients à ${city.name} et en Seine-et-Marne.`} bg="bg-white" />
      <BrandsSection city={city.name} />
      <ArticlesTeaser title={`Conseils & actualités`} subtitle={`Nos guides sécurité utiles aux habitants de ${city.name} et de toute la Seine-et-Marne.`} className="bg-zinc-50" />
      <CtaBand title={`Serrurier à ${city.name} — appelez maintenant`} />
    </div>
  );
}
